import React from 'react'
import styled from 'styled-components'
import { PrimaryButton, SecondaryButton } from '../../styled/button'

const PricingContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
`

const Hero = styled.div`
  text-align: center;
  margin-bottom: 3rem;

  h1 {
    font-size: 2.5rem;
    margin-bottom: 1rem;
    color: ${({ theme }) => theme.bodyText};
  }

  .subtitle {
    font-size: 1.5rem;
    color: ${({ theme }) => theme.primary};
    margin-bottom: 0.5rem;
  }

  .tagline {
    font-size: 1.1rem;
    color: ${({ theme }) => theme.secondaryText};
  }
`

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const PricingCard = styled.div`
  background: ${({ theme }) => theme.secondaryBackground};
  border: 2px solid ${({ theme, featured }) => featured ? theme.primary : theme.borderColor};
  border-radius: 8px;
  padding: 2rem;
  position: relative;
  box-shadow: ${({ featured }) => featured ? '0 8px 24px rgba(0,0,0,0.15)' : '0 2px 8px rgba(0,0,0,0.1)'};
  transform: ${({ featured }) => featured ? 'scale(1.05)' : 'scale(1)'};

  @media (max-width: 768px) {
    transform: scale(1);
  }
`

const Badge = styled.div`
  position: absolute;
  top: -12px;
  right: 20px;
  background: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.primaryContrast};
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: bold;
`

const PlanIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 0.5rem;
`

const PlanName = styled.h2`
  font-size: 1.75rem;
  margin-bottom: 0.5rem;
  color: ${({ theme }) => theme.bodyText};
`

const PlanPrice = styled.div`
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 0.25rem;
  color: ${({ theme }) => theme.primary};

  .period {
    font-size: 1rem;
    font-weight: normal;
    color: ${({ theme }) => theme.secondaryText};
  }
`

const PlanSubtitle = styled.div`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.secondaryText};
  margin-bottom: 1.5rem;
`

const PlanDescription = styled.p`
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.secondaryText};
  line-height: 1.6;
`

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1.5rem 0;

  li {
    padding: 0.5rem 0;
    color: ${({ theme }) => theme.bodyText};
    line-height: 1.5;

    &:before {
      content: '✓ ';
      color: ${({ theme }) => theme.primary};
      font-weight: bold;
      margin-right: 0.5rem;
    }
  }
`

const ButtonGroup = styled.div`
  margin-top: 1.5rem;

  button {
    width: 100%;
    margin-bottom: 0.5rem;
  }
`

const AnnualPricing = styled.div`
  background: ${({ theme }) => theme.background};
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 8px;
  padding: 1.5rem;
  margin: 3rem 0;
  text-align: center;

  h3 {
    color: ${({ theme }) => theme.bodyText};
    margin-bottom: 1rem;
  }

  .savings {
    display: flex;
    justify-content: center;
    gap: 2rem;
    flex-wrap: wrap;

    div {
      padding: 1rem;

      strong {
        color: ${({ theme }) => theme.primary};
        font-size: 1.2rem;
      }
    }
  }
`

const Testimonials = styled.div`
  background: ${({ theme }) => theme.secondaryBackground};
  border-radius: 8px;
  padding: 2rem;
  margin: 3rem 0;

  h3 {
    text-align: center;
    color: ${({ theme }) => theme.bodyText};
    margin-bottom: 2rem;
  }

  .quotes {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;

    blockquote {
      margin: 0;
      padding: 1rem;
      font-style: italic;
      color: ${({ theme }) => theme.secondaryText};
      border-left: 3px solid ${({ theme }) => theme.primary};
    }
  }
`

const FaqSection = styled.div`
  margin: 3rem 0;

  h3 {
    text-align: center;
    color: ${({ theme }) => theme.bodyText};
    margin-bottom: 2rem;
  }

  .faq-item {
    margin-bottom: 1.5rem;

    h4 {
      color: ${({ theme }) => theme.primary};
      margin-bottom: 0.5rem;
    }

    p {
      color: ${({ theme }) => theme.secondaryText};
      line-height: 1.6;
    }
  }
`

const CtaSection = styled.div`
  text-align: center;
  margin: 3rem 0;
  padding: 2rem;

  h3 {
    color: ${({ theme }) => theme.bodyText};
    margin-bottom: 1.5rem;
  }

  .cta-buttons {
    display: flex;
    justify-content: center;
    gap: 1rem;
    flex-wrap: wrap;

    button {
      min-width: 200px;
    }
  }
`

const Pricing = () => {
  return (
    <PricingContainer>
      <Hero>
        <h1>🎸 Simple Pricing for Every Kind of Musician</h1>
        <div className='subtitle'>Play More. Rehearse like a pro. Stay in Sync.</div>
        <p className='tagline'>
          Whether you're practicing alone, running weekly band rehearsals, or booking gigs every weekend —
          Tabify keeps your songs, setlists, and band organized in one place.
        </p>
      </Hero>

      <PricingGrid>
        {/* Solo Player */}
        <PricingCard>
          <PlanIcon>🆓</PlanIcon>
          <PlanName>Solo Player</PlanName>
          <PlanPrice>
            Free Forever
          </PlanPrice>
          <PlanDescription>
            Perfect for learning, writing, and trying Tabify on your own.
          </PlanDescription>

          <strong>You get:</strong>
          <FeatureList>
            <li>Create up to 25 songs</li>
            <li>Build 1 setlist</li>
            <li>Advanced tab, chord, and songwriting tools</li>
            <li>Share with up to 3 people (so your band can try it out)</li>
            <li>Offline Mode (native app) — one-time purchase: $2.99</li>
          </FeatureList>

          <ButtonGroup>
            <PrimaryButton>Start Free</PrimaryButton>
          </ButtonGroup>
        </PricingCard>

        {/* Band Mode */}
        <PricingCard featured>
          <Badge>⭐ Most Popular</Badge>
          <PlanIcon>🎶</PlanIcon>
          <PlanName>Band Mode</PlanName>
          <PlanPrice>
            $5 <span className='period'>/ month</span>
          </PlanPrice>
          <PlanSubtitle>Up to 5 band members</PlanSubtitle>
          <PlanDescription>
            Run rehearsals like a pro. Everyone stays on the same version, every time.
          </PlanDescription>

          <strong>Everything in Solo Player, plus:</strong>
          <FeatureList>
            <li><strong>Synchronized Rehearsal Mode</strong> — One person controls the setlist, every screen updates instantly</li>
            <li><strong>Live Band Collaboration</strong> — Share songs and setlists with up to 20 people or groups</li>
            <li><strong>Smart Notifications</strong> — Know when someone edits, adds, or deletes content</li>
            <li>Create up to 500 songs and 50 setlists</li>
          </FeatureList>

          <ButtonGroup>
            <PrimaryButton>Start 14-Day Band Mode Trial</PrimaryButton>
            <div style={{ fontSize: '0.85rem', color: '#888', marginTop: '0.5rem' }}>
              No credit card required
            </div>
          </ButtonGroup>
        </PricingCard>

        {/* Gigging Band */}
        <PricingCard>
          <PlanIcon>🚐</PlanIcon>
          <PlanName>Gigging Band</PlanName>
          <PlanPrice>
            $10 <span className='period'>/ month</span>
          </PlanPrice>
          <PlanSubtitle>Up to 10 band members</PlanSubtitle>
          <PlanDescription>
            For bands that play out, tour, and take their organization seriously.
          </PlanDescription>

          <strong>Everything in Band Mode, plus:</strong>
          <FeatureList>
            <li><strong>Shared Band Calendar</strong> — Schedule gigs, rehearsals, and deadlines in one place</li>
            <li><strong>Social Media Publishing</strong> — Post to Facebook, Instagram, and Twitter from a single dashboard</li>
            <li><strong>Slack & Discord Integration</strong> — Get updates where your band already chats</li>
            <li><strong>Unlimited songs and setlists</strong></li>
            <li><strong>Version History & Song Backups</strong> — Never lose a great arrangement again</li>
          </FeatureList>

          <ButtonGroup>
            <SecondaryButton>Upgrade to Gigging Band</SecondaryButton>
          </ButtonGroup>
        </PricingCard>
      </PricingGrid>

      <AnnualPricing>
        <h3>💸 Save with Annual Plans</h3>
        <div className='savings'>
          <div>
            <strong>Band Mode:</strong> $50/year <em>(2 months free)</em>
          </div>
          <div>
            <strong>Gigging Band:</strong> $100/year <em>(2 months free)</em>
          </div>
        </div>
      </AnnualPricing>

      <Testimonials>
        <h3>Why Bands Love Tabify</h3>
        <div className='quotes'>
          <blockquote>"Everyone shows up actually prepared."</blockquote>
          <blockquote>"No more 'wait, which song is next?' during rehearsal."</blockquote>
          <blockquote>"We run our entire rehearsal from one tablet now."</blockquote>
        </div>
      </Testimonials>

      <FaqSection>
        <h3>🎵 Frequently Asked Questions</h3>
        <div className='faq-item'>
          <h4>Can my band split the cost?</h4>
          <p>Sorry...billing for a band is handled by a single memeber.</p>
        </div>
        <div className='faq-item'>
          <h4>Do we need to install anything?</h4>
          <p>Web works everywhere. Offline mode is available in the native apps for Android and iOS</p>
        </div>
        <div className='faq-item'>
          <h4>Can I cancel anytime?</h4>
          <p>Yes — no contracts, no hassle.*</p>
        </div>
      </FaqSection>

      <CtaSection>
        <h3>Try It With Your Band — Free</h3>
        <p>Start in Solo Player. Upgrade when you're ready to rehearse smarter, not longer.</p>
        <div className='cta-buttons'>
          <PrimaryButton>Start Free</PrimaryButton>
          <SecondaryButton>Try Band Mode Free for 14 Days</SecondaryButton>
        </div>
      </CtaSection>
      <CtaSection>
        <p><i>* No refunds. Cancelled subscriptions are valid through the end of the paid billing period (annual or monthly)</i></p>
      </CtaSection>
    </PricingContainer>
  )
}

export default Pricing
